import { forwardRef, type InputHTMLAttributes } from 'react';

import { cn } from '~/lib/cn';
import { variants } from '~/lib/variants';

import type { InputRounded, InputSize, InputState, InputType } from './types';

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  type?: InputType;
  size?: InputSize;
  rounded?: InputRounded;
  value?: string | number;
  state?: keyof typeof InputState;
}

const input = variants({
  base: [
    'w-full',
    'outline',
    'outline-1',
    'outline-emphasis-subtle',
    'outline-offset-[-1px]',
    'focus:ring-none',
    'text-body-sm',
    'py-md',
    'px-md',
    'bg-emphasis-default',
    'text-strong',
    'placeholder:text-subtle',
    'resize-none',
    'disabled:cursor-not-allowed',
    'disabled:outline-disabled',
    'disabled:outline-opacity-subtle',
    'disabled:outline-px',
    'disabled:pointer-events-none',
    'disabled:text-disabled',
    'read-only:cursor-not-allowed',
    'read-only:outline-disabled',
    'read-only:outline-opacity-subtle',
    'read-only:outline-px',
    'read-only:pointer-events-none',
    'read-only:text-disabled',
    'hover:outline-opacity-stronger',
  ],
  variants: {
    state: {
      default: [
        'focus:outline-secondary-default',
        'hover:outline-disabled',
        'focus:outline-2',
        'hover:outline-2',
        'hover:outline-offset-[-2px]',
        'focus:outline-offset-[-2px]',
      ],
      error: [
        'outline',
        'outline-error-default',
        'outline-2',
        'outline-offset-[-2px]',
      ],
      warning: [
        'outline',
        'outline-warning-default',
        'outline-2',
        'outline-offset-[-2px]',
      ],
      success: [
        'outline',
        'outline-success-default',
        'outline-2',
        'outline-offset-[-2px]',
      ],
    },
    size: {
      xlarge: ['leading-6'],
      large: ['leading-4'],
    },
    rounded: {
      xsmall: ['rounded-xs'],
      small: ['rounded-sm'],
      medium: ['rounded-md'],
    },
  },
});

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type,
      size = 'large',
      rounded = 'xsmall',
      state = 'default',
      className,
      ...props
    },
    ref,
  ) => {
    const stateClasses = input({
      state,
      rounded,
      size,
    });

    return (
      <input
        aria-invalid={state === 'error'}
        className={cn(stateClasses, className)}
        type={type}
        ref={ref}
        {...props}
      />
    );
  },
);

Input.displayName = 'Input';

export { Input };
